import React from "react";
import type { Release } from "@packages/commons";
import Card from "@imminently/decisively-ui/Card";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import { CardMenu, MenuSlot } from "./_shared";
import { getReleaseUiName } from "@/features/release";
import { useActivateRelease, useDeleteRelease, useExportReleaseModal, useReleaseModal } from "@/features/release";

const formatLastModified = (dateStr: string): string => {
  const date = new Date(dateStr);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}:${get("second")} (${timezone})`;
};

const formatBasedOn = (basedOn?: string | number): string => {
  if (basedOn == null) return "Initial";
  if (typeof basedOn === "number") return `Release ${basedOn}`;
  return basedOn;
};

export interface ReleaseCardProps {
  release: Release;
  onView?: () => void;
}

export const ReleaseCard = ({ release, onView }: ReleaseCardProps) => {
  const name = getReleaseUiName(release);
  const isActive = release.activeVersionFlag;
  const { deleteRelease } = useDeleteRelease();
  const { activateRelease, isLoading: activateLoading } = useActivateRelease();
  const openRelease = useReleaseModal();
  const openExportRelease = useExportReleaseModal();
  const canPromote = release.env === "test";

  return (
    <Card className="p-4 rounded-lg gap-2 group/card hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-semibold text-lg leading-tight truncate">{name}</span>
          {release.description && (
            <span className="text-muted-foreground text-xs font-medium truncate">{release.description}</span>
          )}
        </div>
        <MenuSlot>
          <CardMenu>
            <DropdownMenu.Item onClick={() => openRelease(release, 'edit')}>
              <DropdownMenu.Item.Label>Edit</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="edit" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>
            <DropdownMenu.Item disabled={isActive || activateLoading} onClick={() => activateRelease(release.id)}>
              <DropdownMenu.Item.Label>Make Current</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="star" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>
            {canPromote ? (
              <DropdownMenu.Item onClick={() => openRelease(release, "promote")}>
                <DropdownMenu.Item.Label>Promote</DropdownMenu.Item.Label>
                <DropdownMenu.Item.Icon><Icon name="move-up" size={16} /></DropdownMenu.Item.Icon>
              </DropdownMenu.Item>
            ) : null}
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={() => openRelease(release)}>
              <DropdownMenu.Item.Label>Duplicate</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="duplicate" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => openExportRelease(release)}>
              <DropdownMenu.Item.Label>Export</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="download" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item color="destructive" onClick={() => deleteRelease(release)}>
              <DropdownMenu.Item.Label>Delete</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="delete" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>
          </CardMenu>
        </MenuSlot>
      </div>

      {/* Status + Date */}
      <div className="flex items-start gap-2">
        <div className="flex items-center min-h-5">
          {isActive && <Chip variant="success" size="sm" label="Current" />}
        </div>
        {release.lastModified && (
          <div className="flex flex-col items-end text-sm font-semibold text-end ml-auto">
            <span>{formatLastModified(release.lastModified)}</span>
            <span className="text-muted-foreground capitalize">last modified</span>
          </div>
        )}
      </div>

      {/* Meta + Action */}
      <div className="flex items-end gap-2 h-10">
        <dl className="flex flex-col text-sm flex-1">
          <div className="flex items-baseline gap-1">
            <dt className="text-muted-foreground">Based on</dt>
            <dd className="font-semibold">{formatBasedOn(release.basedOn)}</dd>
          </div>
          {release.promotedAs != null && (
            <div className="flex items-baseline gap-1">
              <dt className="text-muted-foreground">Promoted as</dt>
              <dd className="font-semibold">{release.promotedAs}</dd>
            </div>
          )}
        </dl>
        <Button size="sm" variant="outline" intent="secondary" onClick={onView} label="View">View</Button>
      </div>
    </Card>
  );
};
